import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Typography,
  Row,
  Col,
  Modal,
  Form,
  InputNumber,
  message,
  Tag,
  Tooltip,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  MoneyCollectOutlined,
  UserOutlined,
  SearchOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const { Option } = Select;

// Create axios instance with JWT authentication (for admin)
// Using relative URL to leverage webpack proxy
const adminApi = axios.create({
  baseURL: '/api',
  timeout: 30000,
});

// Request interceptor to add JWT token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired - redirect to login
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

const FeeTierManager = () => {
  const [loading, setLoading] = useState(false);
  const [feeTiers, setFeeTiers] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  // Modal states
  const [tierModalVisible, setTierModalVisible] = useState(false);
  const [calculatorVisible, setCalculatorVisible] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [editingTier, setEditingTier] = useState(false);
  const [selectedProductCategoryCode, setSelectedProductCategoryCode] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    merchant: null,
    product: null,
    category: null,
    search: ''
  });

  const [tierForm] = Form.useForm();
  const [calculatorForm] = Form.useForm();

  useEffect(() => {
    fetchFeeTiers();
    fetchMerchants();
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchFeeTiers = async () => {
    try {
      setLoading(true);
      console.log('Fetching fee tiers...');
      const response = await adminApi.get('/master-products/fee-tiers');
      console.log('Fee tiers response:', response.data);
      setFeeTiers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching fee tiers:', error);
      message.error(error.response?.data?.error || 'Gagal memuat data fee tiers');
    } finally {
      setLoading(false);
    }
  };

  const fetchMerchants = async () => {
    try {
      console.log('Fetching approved merchants...');
      const response = await adminApi.get('/master-products/approved-merchants');
      console.log('Approved merchants response:', response.data);
      setMerchants(response.data.data || []);
    } catch (error) {
      console.error('Error fetching merchants:', error);
      message.error('Gagal memuat data merchant');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await adminApi.get('/master-products/products');
      setProducts(response.data.data || []);
    } catch (error) {
      message.error('Gagal memuat data produk');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await adminApi.get('/master-products/categories');
      setCategories(response.data.data || []);
    } catch (error) {
      message.error('Gagal memuat data kategori');
    }
  };

  const handleCreateTier = async (values) => {
    try {
      setLoading(true);
      await adminApi.post('/master-products/merchant-fee-tiers', values);
      message.success('Fee tier berhasil dibuat');
      setTierModalVisible(false);
      tierForm.resetFields();
      fetchFeeTiers();
    } catch (error) {
      message.error(error.response?.data?.message || 'Gagal membuat fee tier');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTier = async (values) => {
    try {
      setLoading(true);
      await adminApi.put(`/master-products/merchant-fee-tiers/${selectedTier.id}`, values);
      message.success('Fee tier berhasil diperbarui');
      setTierModalVisible(false);
      tierForm.resetFields();
      setSelectedTier(null);
      setEditingTier(false);
      fetchFeeTiers();
    } catch (error) {
      message.error(error.response?.data?.message || 'Gagal memperbarui fee tier');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTier = async (id) => {
    Modal.confirm({
      title: 'Hapus Fee Tier',
      content: 'Apakah Anda yakin ingin menghapus fee tier ini?',
      okText: 'Ya',
      cancelText: 'Batal',
      okType: 'danger',
      onOk: async () => {
        try {
          await adminApi.delete(`/master-products/merchant-fee-tiers/${id}`);
          message.success('Fee tier berhasil dihapus');
          fetchFeeTiers();
        } catch (error) {
          message.error(error.response?.data?.message || 'Gagal menghapus fee tier');
        }
      }
    });
  };

  const handleEditTier = (record) => {
    setSelectedTier(record);
    setEditingTier(true);

    const categoryCode = record.category_code || null;
    setSelectedProductCategoryCode(categoryCode);
    const isQris = categoryCode === 'QRIS';

    tierForm.setFieldsValue({
      merchant_id: record.merchant_id,
      product_id: record.product_id,
      tier_name: record.tier_name,
      min_amount: record.min_amount,
      max_amount: record.max_amount,
      fee_merchant: record.fee_merchant,
      fee_system: record.fee_system,
      fee_type: record.fee_type,
      min_fee: record.min_fee,
      max_fee: record.max_fee,
      priority: record.priority,
      is_active: record.is_active,
      jenis_beban: isQris ? (record.jenis_beban || 'mitra') : 'user'
    });
    setTierModalVisible(true);
  };

  const handleProductChange = (productId) => {
    const product = products.find(p => p.id === productId);
    const categoryCode = product?.category_code || null;
    setSelectedProductCategoryCode(categoryCode);
    if (categoryCode !== 'QRIS') {
      tierForm.setFieldValue('jenis_beban', 'user');
    }
  };

  const handleCalculateFee = async (values) => {
    try {
      const { merchant_id, product_id, amount } = values;
      const response = await adminApi.get(`/master-products/calculate-fee/${merchant_id}/${product_id}?amount=${amount}`);

      Modal.info({
        title: 'Hasil Kalkulasi Fee',
        width: 600,
        content: (
          <div style={{ marginTop: 16 }}>
            <p><strong>Merchant:</strong> {merchants.find(m => m.id === merchant_id)?.full_name}</p>
            <p><strong>Produk:</strong> {response.data.data.product_name}</p>
            <p><strong>Jumlah Transaksi:</strong> Rp {response.data.data.transaction_amount.toLocaleString()}</p>
            <Divider />
            <p><strong>Fee Amount:</strong> <span style={{ color: '#f50', fontSize: '16px', fontWeight: 'bold' }}>Rp {response.data.data.fee_amount.toLocaleString()}</span></p>
            <p><strong>Net Amount:</strong> <span style={{ color: '#52c41a', fontSize: '16px', fontWeight: 'bold' }}>Rp {response.data.data.net_amount.toLocaleString()}</span></p>
            <p><strong>Fee Type:</strong> {response.data.data.fee_type}</p>
            {response.data.data.tier_name && (
              <>
                <Divider />
                <p><strong>Tier Used:</strong> <Tag color="blue">{response.data.data.tier_name}</Tag></p>
                <p><strong>Fee Rate:</strong> {response.data.data.fee_type === 'percentage' ?
                  `${(response.data.data.fee_rate * 100).toFixed(2)}%` :
                  `Rp ${response.data.data.fee_rate.toLocaleString()}`}</p>
              </>
            )}
          </div>
        )
      });
    } catch (error) {
      message.error(error.response?.data?.message || 'Gagal menghitung fee');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getCategoryColor = (categoryCode) => {
    const colors = {
      QRIS: 'blue',
      VA: 'green',
      EWALLET: 'purple',
      TRANSFER: 'orange'
    };
    return colors[categoryCode] || 'default';
  };

  // Apply filters to fee tiers
  const getFilteredTiers = () => {
    return feeTiers.filter(tier => {
      const matchesSearch = !filters.search ||
        tier.tier_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        tier.full_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        tier.product_name?.toLowerCase().includes(filters.search.toLowerCase());

      const matchesMerchant = !filters.merchant || tier.merchant_id === filters.merchant;
      const matchesProduct = !filters.product || tier.product_id === filters.product;
      const matchesCategory = !filters.category || tier.category_code === filters.category;

      return matchesSearch && matchesMerchant && matchesProduct && matchesCategory;
    });
  };

  const tierColumns = [
    {
      title: 'Merchant',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (text) => (
        <div>
          <UserOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          <strong>{text}</strong>
        </div>
      ),
    },
    {
      title: 'Produk',
      dataIndex: 'product_name',
      key: 'product_name',
      render: (text, record) => (
        <div>
          <div><strong>{text}</strong></div>
          <div style={{ marginTop: 4 }}>
            <Tag color="blue" size="small" style={{ fontFamily: 'monospace' }}>
              {record.product_code || record.kode_main}
            </Tag>
          </div>
          <div style={{ marginTop: 2 }}>
            <Tag color={getCategoryColor(record.category_code)} size="small">
              {record.category_code}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: 'Tier Info',
      dataIndex: 'tier_name',
      key: 'tier_name',
      render: (text, record) => (
        <div>
          <Tag color="blue">{text}</Tag>
          <div style={{ fontSize: '11px', color: '#666', marginTop: 2 }}>
            Priority: {record.priority}
          </div>
        </div>
      ),
    },
    {
      title: 'Amount Range',
      key: 'amount_range',
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div><strong>Min:</strong> {formatCurrency(record.min_amount)}</div>
          <div><strong>Max:</strong> {record.max_amount ? formatCurrency(record.max_amount) : 'Unlimited'}</div>
        </div>
      ),
    },
    {
      title: 'Fee Configuration',
      key: 'fee',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold', color: '#f50' }}>
            {record.fee_type === 'percentage' ?
              `${(record.fee_merchant * 100).toFixed(2)}%` :
              formatCurrency(record.fee_merchant)}
          </div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            <div>Min: {formatCurrency(record.min_fee)}</div>
            {record.max_fee > 0 && <div>Max: {formatCurrency(record.max_fee)}</div>}
          </div>
        </div>
      ),
    },
    {
      title: 'Effective Period',
      key: 'effective_period',
      render: (_, record) => (
        <div style={{ fontSize: '11px' }}>
          <div>From: {new Date(record.effective_from).toLocaleDateString()}</div>
          {record.effective_to && (
            <div>To: {new Date(record.effective_to).toLocaleDateString()}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Aktif' : 'Tidak Aktif'}
        </Tag>
      ),
    },
    {
      title: 'Aksi',
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit Fee Tier">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditTier(record)}
            />
          </Tooltip>
          <Tooltip title="Hapus Fee Tier">
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteTier(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Fee Tiers Management"
        extra={
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingTier(false);
                setSelectedTier(null);
                tierForm.resetFields();
                setTierModalVisible(true);
              }}
            >
              Tambah Fee Tier
            </Button>
            <Button
              icon={<MoneyCollectOutlined />}
              onClick={() => setCalculatorVisible(true)}
            >
              Test Kalkulasi
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchFeeTiers}
            />
          </Space>
        }
      >
        <div style={{ marginBottom: '16px' }}>
          <Text type="secondary">
            Konfigurasi fee berbeda berdasarkan jumlah transaksi untuk setiap merchant dan produk.
            Fee tier dengan priority lebih tinggi akan diprioritaskan.
          </Text>
        </div>

        {/* Filters */}
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={6}>
            <Input
              placeholder="Cari tier, merchant, produk..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              allowClear
            />
          </Col>
          <Col xs={24} sm={6}>
            <Select
              placeholder="Filter Merchant"
              style={{ width: '100%' }}
              value={filters.merchant}
              onChange={(value) => setFilters(prev => ({ ...prev, merchant: value }))}
              allowClear
            >
              {merchants.map(merchant => (
                <Option key={merchant.id} value={merchant.id}>
                  {merchant.full_name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <Select
              placeholder="Filter Produk"
              style={{ width: '100%' }}
              value={filters.product}
              onChange={(value) => setFilters(prev => ({ ...prev, product: value }))}
              allowClear
            >
              {products.map(product => (
                <Option key={product.id} value={product.id}>
                  {product.product_name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <Select
              placeholder="Filter Kategori"
              style={{ width: '100%' }}
              value={filters.category}
              onChange={(value) => setFilters(prev => ({ ...prev, category: value }))}
              allowClear
            >
              {categories.map(category => (
                <Option key={category.category_code} value={category.category_code}>
                  {category.category_name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Table
          columns={tierColumns}
          dataSource={getFilteredTiers()}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} dari ${total} fee tiers`
          }}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* Fee Tier Create/Edit Modal */}
      <Modal
        title={editingTier ? 'Edit Fee Tier' : 'Tambah Fee Tier Baru'}
        open={tierModalVisible}
        onCancel={() => {
          setTierModalVisible(false);
          tierForm.resetFields();
          setSelectedTier(null);
          setEditingTier(false);
          setSelectedProductCategoryCode(null);
        }}
        onOk={() => tierForm.submit()}
        confirmLoading={loading}
        width={800}
      >
        <Form
          form={tierForm}
          layout="vertical"
          onFinish={editingTier ? handleUpdateTier : handleCreateTier}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="merchant_id"
                label="Merchant"
                rules={[{ required: true, message: 'Merchant wajib dipilih!' }]}
              >
                <Select placeholder="Pilih merchant" disabled={editingTier}>
                  {merchants.map(merchant => (
                    <Option key={merchant.id} value={merchant.id}>
                      {merchant.full_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="product_id"
                label="Produk"
                rules={[{ required: true, message: 'Produk wajib dipilih!' }]}
              >
                <Select
                  placeholder="Cari / pilih produk"
                  disabled={editingTier}
                  onChange={handleProductChange}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children ?? '').toString().toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {products.map(product => (
                    <Option key={product.id} value={product.id}>
                      {`${product.product_name} (${product.product_code})`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="tier_name"
                label="Nama Tier"
                rules={[{ required: true, message: 'Nama tier wajib diisi!' }]}
              >
                <Input placeholder="e.g., Small Transaction" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="priority"
                label="Priority"
                rules={[{ required: true, message: 'Priority wajib diisi!' }]}
                initialValue={1}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={1}
                  max={10}
                  placeholder="1-10 (higher = prioritized)"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="is_active"
                label="Status"
                initialValue={true}
              >
                <Select>
                  <Option value={true}>Aktif</Option>
                  <Option value={false}>Tidak Aktif</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider>Amount Range Configuration</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="min_amount"
                label="Minimum Amount (IDR)"
                rules={[{ required: true, message: 'Minimum amount wajib diisi!' }]}
                initialValue={0}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="max_amount"
                label="Maximum Amount (IDR) - Kosongkan untuk unlimited"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  placeholder="Kosongkan untuk unlimited"
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Fee Configuration</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="fee_type"
                label="Fee Type"
                rules={[{ required: true, message: 'Fee type wajib dipilih!' }]}
                initialValue="percentage"
              >
                <Select>
                  <Option value="flat">Flat Fee</Option>
                  <Option value="percentage">Percentage</Option>
                  <Option value="mixed">Mixed</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="fee_merchant"
                label="Fee Merchant"
                rules={[{ required: true, message: 'Fee merchant wajib diisi!' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.0001}
                  precision={4}
                  placeholder="0.0150 untuk 1.5% atau 5000 untuk Rp 5,000"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="jenis_beban"
                label="Beban Fee"
                initialValue={selectedProductCategoryCode === 'QRIS' ? 'mitra' : 'user'}
                tooltip={
                  selectedProductCategoryCode === 'QRIS'
                    ? 'Siapa yang menanggung fee: user (ditambah ke nominal QR) atau mitra (dipotong dari saldo mitra)'
                    : 'Produk ini hanya mendukung beban ke user (fee ditambahkan ke nominal tagihan customer)'
                }
              >
                <Select disabled={selectedProductCategoryCode !== null && selectedProductCategoryCode !== 'QRIS'}>
                  {selectedProductCategoryCode === 'QRIS' && (
                    <Option value="mitra">Mitra (dipotong saldo)</Option>
                  )}
                  <Option value="user">User (ditambah ke nominal)</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="min_fee"
                label="Minimum Fee (IDR)"
                initialValue={0}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="max_fee"
                label="Maximum Fee (IDR) - 0 untuk unlimited"
                initialValue={0}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Fee Calculator Modal */}
      <Modal
        title="Test Kalkulasi Fee"
        open={calculatorVisible}
        onCancel={() => setCalculatorVisible(false)}
        onOk={() => calculatorForm.submit()}
        width={500}
      >
        <Form
          form={calculatorForm}
          layout="vertical"
          onFinish={handleCalculateFee}
        >
          <Form.Item
            name="merchant_id"
            label="Merchant"
            rules={[{ required: true, message: 'Merchant wajib dipilih!' }]}
          >
            <Select placeholder="Pilih merchant">
              {merchants.map(merchant => (
                <Option key={merchant.id} value={merchant.id}>
                  {merchant.full_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="product_id"
            label="Produk"
            rules={[{ required: true, message: 'Produk wajib dipilih!' }]}
          >
            <Select placeholder="Pilih produk">
              {products.map(product => (
                <Option key={product.id} value={product.id}>
                  {product.product_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="amount"
            label="Jumlah Transaksi (IDR)"
            rules={[{ required: true, message: 'Jumlah transaksi wajib diisi!' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              placeholder="Masukkan jumlah transaksi"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FeeTierManager;// Force rebuild - Fri Jan  2 03:33:32 CET 2026
// Force rebuild at 1767321792
// Another change at 1767321792
